import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Calender } from '../../assets/icons/calender.svg';
import { ReactComponent as LeftArrow } from '../../assets/icons/left-arrow.svg';
import { ReactComponent as RightArrow } from '../../assets/icons/right-arrow.svg';
import { changeDateFormat } from '../../shared-functions/DateTime';

/**
 * CalendarGrid
 * @param param0
 * @returns
 */
function CalendarGrid({ days, onDayClick, getDayClass, onHover }: any) {
  return (
    <div className="calender-body">
      <div className="calender-header-items">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
          <div key={d} className="header-item-label">
            {d}
          </div>
        ))}
      </div>
      <div className="calender-body-items">
        {days.map((d: any, index: number) => (
          <div
            key={index}
            role="presentation"
            className={`day-item ${Object.keys(d).length === 0 ? 'empty' : ''} ${d.fullDate ? getDayClass(d.fullDate) : ''}`}
            onClick={() => d.date && onDayClick(d.date, d.fullDate)}
            onMouseEnter={() => d.date && onHover(d.date)}>
            {d.number}
          </div>
        ))}
      </div>
    </div>
  );
}

interface CustomDateRangePickerProperties {
  inputLabel?: string;
  startDate: string;
  endDate: string;
  onChange: (start: string, end: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  valueRequired?: boolean;
}

const presets = [
  { label: 'This month', key: 'this_month' },
  { label: 'One month', key: 'one_month' }, // Assuming last 30 days
  { label: 'Prev month', key: 'prev_month' },
  { label: 'This week', key: 'this_week' },
  { label: 'Last 7 days', key: 'last_7_days' },
  { label: 'Prev week', key: 'prev_week' },
  { label: 'Prev 2 weeks', key: 'prev_2_weeks' },
  { label: 'Last 14 days', key: 'last_14_days' },
  { label: 'Custom Range', key: 'custom' },
];

const monthsList = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * This function is used to render the custom date range picker.
 * @param {string} inputLabel - The label for the input field.
 * @param {string} startDate - The start date for the date range.
 * @param {string} endDate - The end date for the date range.
 * @param {function} onChange - The function to be called when the date range changes.
 * @param {string} placeholder - The placeholder for the input field.
 * @param {boolean} readOnly - The read only flag for the input field.
 * @param {boolean} valueRequired - The value required flag for the input field.
 * @returns {JSX.Element} The custom date range picker.
 */
function CustomDateRangePicker({
  inputLabel,
  startDate,
  endDate,
  onChange,
  placeholder,
  readOnly,
  valueRequired,
}: CustomDateRangePickerProperties) {
  const reference = useRef<HTMLDivElement>(null);
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<any>({});

  // View state for the LEFT calendar
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [viewYear, setViewYear] = useState(new Date().getFullYear());

  const [activePreset, setActivePreset] = useState('custom');
  const [temporaryStartDate, setTemporaryStartDate] = useState(startDate);
  const [temporaryEndDate, setTemporaryEndDate] = useState(endDate);
  const [hoverDate, setHoverDate] = useState<string | null>(null);

  // Calendar Data
  const [leftCalendarDays, setLeftCalendarDays] = useState<any[]>([]);
  const [rightCalendarDays, setRightCalendarDays] = useState<any[]>([]);

  // Initialize view based on current startDate or today
  useEffect(() => {
    if (startDate) {
      const date = new Date(startDate);
      if (!Number.isNaN(date.getTime())) {
        setViewMonth(date.getMonth());
        setViewYear(date.getFullYear());
      }
    }
  }, [startDate, open]);

  // Update temp state when props change
  useEffect(() => {
    setTemporaryStartDate(startDate);
    setTemporaryEndDate(endDate);
  }, [startDate, endDate]);

  /**
   * Generates an array of day objects for a given month and year.
   * @param {number} month - The month (0-11).
   * @param {number} year - The year.
   * @returns {Array<Object>} An array of day objects.
   */
  const generateDays = (month: number, year: number) => {
    const date = new Date(year, month, 1);
    const days = [];
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    const formattedDays = days.map((d) => ({
      date: changeDateFormat(d.toString()), // e.g. "01-Jan-2024" depending on util
      fullDate: d,
      number: d.getDate(),
      day: d.getDay(),
    }));

    // Fill padding for empty start slots
    if (formattedDays.length > 0 && formattedDays[0].day > 0) {
      const paddingCount = formattedDays[0].day;
      const padding = Array(paddingCount).fill({});
      return [...padding, ...formattedDays];
    }
    return formattedDays;
  };

  useEffect(() => {
    setLeftCalendarDays(generateDays(viewMonth, viewYear));

    // Right calendar is next month
    let rightMonth = viewMonth + 1;
    let rightYear = viewYear;
    if (rightMonth > 11) {
      rightMonth = 0;
      rightYear += 1;
    }
    setRightCalendarDays(generateDays(rightMonth, rightYear));
  }, [viewMonth, viewYear]);

  /**
   * Handles click outside the date picker component.
   * @param {MouseEvent} event - The mouse event.
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  /**
   * Toggles the open state of the date picker.
   * @param {MouseEvent} event - The mouse event.
   */
  const toggleOpen = (event: any) => {
    if (readOnly) return;
    setOpen(!open);
    if (!open) {
      // Calculate position
      let rect;
      if (event.target.classList.contains('custom-input-container')) {
        rect = event.target.getBoundingClientRect();
      } else {
        rect = event.target.closest('.custom-input-container')?.getBoundingClientRect();
      }

      if (rect) {
        const POPUP_HEIGHT = 450; // Safety max height for decision
        const POPUP_WIDTH = 690;
        const SCREEN_HEIGHT = window.innerHeight;
        const SCREEN_WIDTH = window.innerWidth;

        const styleObject: any = {};
        let left = rect.left + window.scrollX;

        // Horizontal adjustment
        if (rect.left + POPUP_WIDTH > SCREEN_WIDTH) {
          // If overflows right, try to align right edge with screen margin or input right
          const diff = rect.left + POPUP_WIDTH - SCREEN_WIDTH;
          left -= diff + 30;
        }
        if (left < 0) left = 10;

        styleObject.left = left;

        // Vertical adjustment (Flip Up vs Down)
        const spaceBelow = SCREEN_HEIGHT - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow < POPUP_HEIGHT && spaceAbove > spaceBelow) {
          // Flip Up
          styleObject.bottom = SCREEN_HEIGHT - rect.top + 10;
          styleObject.top = 'auto'; // Clear top
        } else {
          // Default Down
          styleObject.top = rect.bottom + window.scrollY + 10; // +10 margin
          styleObject.bottom = 'auto';
        }

        setStyle(styleObject);
      }
    }
  };

  /**
   * Handles month change.
   * @param {string} direction - The direction of the month change.
   */
  const handleMonthChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (viewMonth === 0) {
        setViewMonth(11);
        setViewYear(viewYear - 1);
      } else {
        setViewMonth(viewMonth - 1);
      }
    } else if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  /**
   * Handles preset click.
   * @param {string} key - The key of the preset.
   */
  const handlePresetClick = (key: string) => {
    setActivePreset(key);

    if (key === 'custom') return;

    const today = new Date();
    let start = new Date();
    let end = new Date();

    switch (key) {
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'one_month': // Last 30 days
        start = new Date();
        start.setDate(today.getDate() - 30);
        break;
      case 'prev_month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'this_week':
        start = new Date(today.setDate(today.getDate() - today.getDay()));
        end = new Date(today.setDate(today.getDate() - today.getDay() + 6));

        break;
      case 'last_7_days':
        start = new Date();
        start.setDate(today.getDate() - 6);
        end = new Date();
        break;
      case 'prev_week':
        {
          // Previous full week (Sunday to Saturday)
          const lastSunday = new Date(today.setDate(today.getDate() - today.getDay()));
          start = new Date(lastSunday.setDate(lastSunday.getDate() - 7));
          end = new Date(new Date(start).setDate(start.getDate() + 6));
        }
        break;
      case 'prev_2_weeks':
        {
          // Last 2 full weeks? or just last 14 days? "Prev 2 weeks" likely means the 2 weeks prior to this week?
          // Let's implement as "Last 14 days" shifted back??
          // Or maybe simply last 14 days.
          // Based on list, 'Last 14 days' is separate.
          // Lets assume Previous 2 weeks means the 2 weeks before the current week.
          const lastSunday = new Date(today.setDate(today.getDate() - today.getDay()));
          end = new Date(lastSunday.setDate(lastSunday.getDate() - 1)); // Sat before this week
          start = new Date(new Date(end).setDate(end.getDate() - 13));
        }
        break;
      case 'last_14_days':
        start = new Date();
        start.setDate(today.getDate() - 13);
        end = new Date();
        break;
      default:
        // Custom
        return;
    }

    const sString = changeDateFormat(start.toString());
    const eventString = changeDateFormat(end.toString());

    setTemporaryStartDate(sString);
    setTemporaryEndDate(eventString);
    onChange(sString, eventString);
    setOpen(false); // Auto close on preset

    // Update view to start date
    setViewMonth(start.getMonth());
    setViewYear(start.getFullYear());
  };

  //   const isDateInRange = (dateStr: string) => {
  //     // Simplify comparison by using timestamps of normalized dates if needed,
  //     // but if string format is consistent (DD-MMM-YYYY), we need to parse.
  //     // Assuming changeDateFormat returns consistent format.
  //     // Better to use the 'fullDate' from the object for comparison
  //     return false; // Handled in render map
  //   };

  /**
   * Handles date click.
   * @param {string} dateString - The date string.
   * @param {Date} dateObject - The date object.
   */
  const handleDateClick = (dateString: string, dateObject: Date) => {
    setActivePreset('custom');

    // If we have both, start over
    if (temporaryStartDate && temporaryEndDate) {
      setTemporaryStartDate(dateString);
      setTemporaryEndDate('');
      return;
    }

    // If we have start only
    if (temporaryStartDate && !temporaryEndDate) {
      const start = new Date(temporaryStartDate);
      if (dateObject < start) {
        setTemporaryStartDate(dateString); // New start
      } else {
        setTemporaryEndDate(dateString); // Set end
        onChange(temporaryStartDate, dateString);
        // Do NOT close, let user see or close manually? Usually auto-close or confirm.
        // Given the design has just a popup, maybe auto close is annoying if they want to adjust.
        // But 'onChange' implies commit.
        // Let's keep it open for a moment or close?
        // Standard behavior: close after selection complete.
        setOpen(false);
      }
      return;
    }

    // If we have nothing
    setTemporaryStartDate(dateString);
  };

  /**
   * Gets the class name for a date cell.
   * @param {Date | undefined} dateObject - The date object.
   * @returns {string} The class name.
   */
  const getDayClass = (dateObject: Date | undefined) => {
    if (!dateObject) return 'empty';
    // const time = dateObject.getTime(); // normalize time part if needed? assume generatDays produces 00:00:00
    // clear time
    dateObject.setHours(0, 0, 0, 0);
    const time = dateObject.getTime();

    let sTime = 0;
    let eventTime = 0;

    if (temporaryStartDate) {
      const d = new Date(temporaryStartDate);
      d.setHours(0, 0, 0, 0);
      sTime = d.getTime();
    }
    if (temporaryEndDate) {
      const d = new Date(temporaryEndDate);
      d.setHours(0, 0, 0, 0);
      eventTime = d.getTime();
    }

    if (sTime && eventTime) {
      if (time === sTime && time === eventTime) return 'selected start-end';
      if (time === sTime) return 'selected start';
      if (time === eventTime) return 'selected end';
      if (time > sTime && time < eventTime) return 'in-range';
    } else if (sTime) {
      if (time === sTime) return 'selected start';
      if (hoverDate) {
        const hDate = new Date(hoverDate);
        hDate.setHours(0, 0, 0, 0);
        const hTime = hDate.getTime();
        if (time > sTime && time <= hTime) return 'in-range-preview';
      }
    }

    return '';
  };

  /**
   * Handles backdrop click.
   * @param {React.MouseEvent} event - The mouse event.
   */
  const handleBackdropClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(false);
  };

  return (
    <div className="custom-input-date-wrap">
      {inputLabel && (
        <p className="input-label">
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}

      <div className="custom-date-input">
        <div className="custom-input-container" onClick={toggleOpen} role="presentation">
          <div className="value-view">
            {startDate && endDate
              ? `${startDate} - ${endDate}`
              : placeholder || 'DD-MMM-YYYY - DD-MMM-YYYY'}
          </div>
          <span className="picker-icon">
            <Calender />
          </span>
        </div>

        {open &&
          ReactDOM.createPortal(
            <div className="date-picker-portal-container">
              <div
                className="date-picker-backdrop"
                role="presentation"
                onClick={handleBackdropClick}
              />
              <div className="calender-box date-range-picker-popup" ref={reference} style={style}>
                {/* Sidebar */}
                <div className="preset-sidebar">
                  {presets.map((p) => (
                    <div
                      key={p.key}
                      role="presentation"
                      className={`preset-item ${activePreset === p.key ? 'active' : ''}`}
                      onClick={() => handlePresetClick(p.key)}>
                      {p.label}
                    </div>
                  ))}
                </div>

                {/* Calendars Container */}
                <div className="dual-calendar-container">
                  {/* Left Calendar */}
                  <div className="calendar-single">
                    <div className="calender-header">
                      <div
                        className="calender-header-left"
                        role="presentation"
                        onClick={() => handleMonthChange('prev')}>
                        <LeftArrow
                          style={{ transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'none' }}
                        />
                      </div>
                      <div className="calender-header-center">
                        {monthsList[viewMonth]} {viewYear}
                      </div>
                      <div className="calender-header-right" style={{ visibility: 'hidden' }}>
                        {/* Hidden arrow to balance layout */}
                        <RightArrow
                          style={{ transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'none' }}
                        />
                      </div>
                    </div>
                    <CalendarGrid
                      days={leftCalendarDays}
                      onDayClick={handleDateClick}
                      getDayClass={getDayClass}
                      onHover={setHoverDate}
                    />
                  </div>

                  {/* Right Calendar */}
                  <div className="calendar-single">
                    <div className="calender-header">
                      <div className="calender-header-left" style={{ visibility: 'hidden' }}>
                        <LeftArrow
                          style={{ transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'none' }}
                        />
                      </div>
                      <div className="calender-header-center">
                        {monthsList[viewMonth + 1 > 11 ? 0 : viewMonth + 1]}{' '}
                        {viewMonth + 1 > 11 ? viewYear + 1 : viewYear}
                      </div>
                      <div
                        className="calender-header-right"
                        role="presentation"
                        onClick={() => handleMonthChange('next')}>
                        <RightArrow
                          style={{ transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'none' }}
                        />
                      </div>
                    </div>
                    <CalendarGrid
                      days={rightCalendarDays}
                      onDayClick={handleDateClick}
                      getDayClass={getDayClass}
                      onHover={setHoverDate}
                    />
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}

CustomDateRangePicker.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  placeholder: 'DD-MMM-YYYY - DD-MMM-YYYY',
  readOnly: false,
};

export default CustomDateRangePicker;
